import React, { useState } from 'react';
import BEM from '../../bem';
import tzTime from '../../utils/tzTime';
import { useCompleteTaskMutation, useReopenTaskMutation } from 'redux-stores/tasks';
import type { Task } from 'js-sdk/src/types/Tasks';

const classes = BEM.with('MessageTaskCard');

type MessageTaskCardProps = {
  task: Task;
  organizationId: string;
};

/**
 * Task card rendered on the source message. Supports the open and completed
 * states from the mockups (strikethrough + "Done"). Completing/reopening is
 * wired inline here because web has no Task Details screen (separate ticket).
 */
export default function MessageTaskCard({ task, organizationId }: MessageTaskCardProps) {
  const [completeTask, { isLoading: isCompleting }] = useCompleteTaskMutation();
  const [reopenTask, { isLoading: isReopening }] = useReopenTaskMutation();
  const [toggleError, setToggleError] = useState<string | null>(null);

  const isCompleted = task.status === 'completed';
  const isBusy = isCompleting || isReopening;

  const toggleComplete = async () => {
    if (isBusy) return;
    setToggleError(null);
    try {
      if (isCompleted) {
        await reopenTask({ organizationId, taskId: task.id }).unwrap();
      } else {
        await completeTask({ organizationId, taskId: task.id }).unwrap();
      }
    } catch {
      setToggleError(isCompleted ? 'Could not reopen task.' : 'Could not complete task.');
    }
  };

  const dueLabel = task.due_at ? `Due ${tzTime(task.due_at).format('MMM D, h:mm A')}` : null;

  return (
    <div className={classes('', { completed: isCompleted })} data-test-id="message-task-card">
      <button
        aria-checked={isCompleted}
        aria-label={isCompleted ? 'Reopen task' : 'Complete task'}
        className={classes('checkbox', { checked: isCompleted })}
        data-test-id="message-task-card-checkbox"
        disabled={isBusy}
        onClick={toggleComplete}
        role="checkbox"
        type="button"
      >
        {isCompleted && (
          <svg aria-hidden className={classes('checkmark')} viewBox="0 0 12 12">
            <path
              d="M10 3L4.5 8.5L2 6"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.6"
            />
          </svg>
        )}
      </button>
      <div className={classes('details')}>
        <div className={classes('title', { completed: isCompleted })}>{task.title}</div>
        {dueLabel && <div className={classes('due')}>{dueLabel}</div>}
      </div>
      {isCompleted && <span className={classes('done-label')}>Done</span>}
      {toggleError && (
        <div
          className={classes('toggle-error')}
          data-test-id="message-task-card-error"
          role="alert"
        >
          {toggleError}
        </div>
      )}
    </div>
  );
}
